import { combineEpics } from 'redux-observable';
import { nanoid } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { combineLatest, filter, from, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs';

import { byId, catchAndLogError, pluckPayload } from 'utils/common';
import { notNull } from 'utils/type_guards';

import module from './index';

import * as api from './cart_product_editor.api';

import cropsModule from 'domains/crops';
import subcatalogsModule from 'domains/subcatalogs';
import productsModule from 'domains/products';
import optionGroupModule from 'domains/option_groups';
import photoModule from 'domains/photos';
import navigation from 'domains/navigation';
import cartProducts from 'domains/cart_products';
import textNodes from 'domains/text_nodes';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { CheckoutRootState } from 'store/checkout_store';
import type { RootState } from 'store/wos_store';
import type { CanvasOptionConfiguration } from 'domains/canvases';
import type { TextNodeEntity } from 'domains/text_nodes';

const { actions } = module;

type CartProductEpic = Epic<Action, Action, RootState | CheckoutRootState>;

/**
 * Fetch the cart product, this endpoint provides more data
 * than we have from the index endpoint
 */
const editCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.pending.match),
    map(pluckPayload),
    mergeMap((id) =>
      from(api.find(id)).pipe(map(actions.cartProductReady), catchAndLogError(actions.rejected()))
    )
  );

/** Fetch the subcatalogs when we have the cart product */
const subcatalogsForCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.cartProductReady.match),
    map(({ payload }) => payload.catalog_api_id),
    map(subcatalogsModule.actions.fetchSubcatalogs.pending)
  );

/** Fetch the optionGroups when we have the cart product */
const optionsForCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.cartProductReady.match),
    map(({ payload }) => payload.product_api_id),
    map(optionGroupModule.actions.fetchOptionGroups.pending)
  );

/**
 * Fetch all photos
 */
const photosForCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.pending.match),
    map(() => photoModule.actions.getAll.pending())
  );

/**
 * Build a valid canvas from the data on a cart product
 */
const canvasForCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.pending.match),
    switchMap(() =>
      combineLatest([
        action$.pipe(filter(actions.cartProductReady.match), take(1), map(pluckPayload)),
        action$.pipe(
          filter(optionGroupModule.actions.fetchOptionGroups.fulfilled.match),
          take(1),
          map(pluckPayload)
        ),
      ])
    ),
    map(([cp, optionGroupResponse]) => {
      const options = optionGroupResponse.optionGroups.flatMap((og) => og.options);

      const canvasOptions = cp.options.reduce((acc: CanvasOptionConfiguration, cpo) => {
        const option = options.find((o) => o.id === cpo.option_api_id);
        if (!option) throw new Error('No Matching Catalog Option for this API ID');

        acc[option.parent_id] = option.id;
        return acc;
      }, {});

      // Crate options are not saved on the cart product, but as they are required we need to
      // get them back on the canvas so it is considered valid
      const includedOptionGroupIds = Object.keys(canvasOptions);
      optionGroupResponse.optionGroups
        .filter((og) => og.required && !includedOptionGroupIds.includes(og.id.toString()))
        .forEach((og) => {
          canvasOptions[og.id] = optionGroupModule.utils.findDefaultOptionId(og.options);
        });

      return {
        id: nanoid(),
        cartProductId: cp.id,
        catalog: cp.catalog_api_id,
        subcatalog: cp.sub_catalog_api_id,
        product: cp.product_api_id,
        options: canvasOptions,
        position: { x: 0, y: 0 },
        rotation: cp.canvas_rotation,
        userRotated: true,
        borderColor: cp.border_color || undefined,
        height: cp.custom_size.height ? parseFloat(cp.custom_size.height) : undefined,
        width: cp.custom_size.width ? parseFloat(cp.custom_size.width) : undefined,
      };
    }),
    map(actions.canvasReady)
  );

/**
 * Set up crops for a cart product
 */
const cropsForCartProductEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.pending.match),
    switchMap(() =>
      combineLatest([
        action$.pipe(filter(actions.cartProductReady.match), take(1), map(pluckPayload)),
        action$.pipe(filter(actions.canvasReady.match), take(1), map(pluckPayload)),
        action$.pipe(filter(subcatalogsModule.actions.fetchSubcatalogs.fulfilled.match), take(1)),
        action$.pipe(filter(photoModule.actions.getAll.fulfilled.match), take(1)),
      ])
    ),
    withLatestFrom(state$),
    map(([[cp, canvas], state]) => {
      const product = productsModule.selectors.selectById(state, cp.product_api_id);
      if (!product) throw new Error('No Matching Catalog Product for this API ID');
      const nodesById = keyBy(product.nodes, 'id');

      return cp.photos.map((cpp) => ({
        crop: cpp.crop,
        filter: cropsModule.utils.cartProductFilterToCropFilter(cpp.style_filter),
        canvasId: canvas.id,
        uid: nodesById[cpp.node_id].uid,
        photoId: Number(cpp.external_asset_id),
      }));
    }),
    map((cps) =>
      cps.length > 0
        ? cropsModule.actions.restoreCartProductPhotoCrops.pending(cps)
        : cropsModule.actions.restoreCartProductPhotoCrops.fulfilled([])
    )
  );

/**
 * Set the active collection when loading a cart product's photos
 */
const assignEditCartProductCollection: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.pending.match),
    switchMap(() =>
      combineLatest([
        action$.pipe(filter(actions.cartProductReady.match), take(1), map(pluckPayload)),
        action$.pipe(
          filter(photoModule.actions.getAll.fulfilled.match),
          take(1),
          map(pluckPayload)
        ),
      ])
    ),
    filter(([cp, photos]) => cp.photos.length > 0),
    map(([cp, photos]) => ({ id: Number(cp.photos[0].external_asset_id), photos })),
    map(({ id, photos }) => photos.find(byId(id))),
    filter(notNull),
    map((photo) => photoModule.actions.setActiveCollection(photo.collection))
  );

const textNodesForCartProductEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.pending.match),
    switchMap(() =>
      combineLatest([
        action$.pipe(filter(actions.cartProductReady.match), take(1), map(pluckPayload)),
        action$.pipe(filter(subcatalogsModule.actions.fetchSubcatalogs.fulfilled.match), take(1)),
      ])
    ),
    withLatestFrom(state$),
    map(([[cp], state]) => {
      const product = productsModule.selectors.selectById(state, cp.product_api_id);
      if (!product) throw new Error('No Matching Catalog Product for this API ID');
      const nodesById = keyBy(product.nodes, 'id');

      return cp.text_nodes.map(
        (tn): TextNodeEntity => ({
          id: nodesById[tn.node_id].uid,
          fontFamily: tn.font_family,
          fontSize: tn.font_size,
          textColor: tn.text_color,
          horizontalAlignment: tn.text_horizontal_alignment,
          verticalAlignment: tn.text_vertical_alignment,
          textContent: tn.text_content,
          autofit: tn.auto_fit,
        })
      );
    }),
    filter((tns) => tns.length > 0),
    map(textNodes.actions.addCartProductTextNodes)
  );

/** Dispatch action acknowledging we have all the data we need */
const completeEditCartProductSetupEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.pending.match),
    switchMap(() =>
      combineLatest([
        action$.pipe(filter(actions.cartProductReady.match), take(1)),
        action$.pipe(filter(actions.canvasReady.match), take(1)),
        action$.pipe(
          filter(cropsModule.actions.restoreCartProductPhotoCrops.fulfilled.match),
          take(1)
        ),
      ])
    ),
    map(() => actions.setupComplete())
  );

/**
 * Since navigation is handled in a thunk dispatched from a component,
 * we have to listen to the underlying navigation action to reset the
 * edit setup status so we can navigate back to the cart page
 */
const resetEditCartProductEpic: CartProductEpic = (action$) =>
  action$.pipe(
    filter(actions.setupComplete.match),
    switchMap(() =>
      action$.pipe(
        filter(navigation.actions.updateCurrentRoute.match),
        filter(({ payload }) => payload === 'editor'),
        map(() => actions.resetEditStatus())
      )
    )
  );

const combined = combineEpics(
  editCartProductEpic,
  subcatalogsForCartProductEpic,
  optionsForCartProductEpic,
  photosForCartProductEpic,
  canvasForCartProductEpic,
  cropsForCartProductEpic,
  textNodesForCartProductEpic,
  completeEditCartProductSetupEpic,
  resetEditCartProductEpic,
  assignEditCartProductCollection
);
export default combined;
